// src/views/UserViews/insights/components/ActiveUsersDialog.tsx
//
// Opened from the Fleet Status "Active Now" card. Lists the same live, SignalR-backed users
// that card counts (see useLiveActiveUsers.ts), so rows keep updating while it is open.
// Visual reference: the "Live Users" frames in the CompassPro-Mobile-Apps Figma file
// (node 3618:3577) - populated list and empty state.

import { Box, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import BaseModal from '@/components/modals/BaseModal';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  compareLiveActiveUsersByName,
  type LiveActiveUser,
} from '@/models/Insights/liveActiveUsers';
import { DASHBOARD_COLORS } from '@/views/UserViews/home/user_dashboard/user_dashboard_components/styles';

// Fixed so the dialog keeps one size whether it lists 1 user or 100 - longer lists scroll.
const LIST_HEIGHT = 240; // px
// BaseModal's Dialog sizes to its content; this pins the paper to the full `xs` width instead.
const FULL_WIDTH_PAPER_SX = { '& .MuiDialog-paper': { width: '100%' } };
const GROUP_COLUMN_WIDTH = 140; // px
const LIVE_DOT_SIZE = 8; // px
const LIVE_DOT_PULSE_DURATION_MS = 2000;
// Peak stays under the layout size so the largest frame is a 6px dot.
const LIVE_DOT_PULSE_MAX_SCALE = 0.75;
const LIVE_DOT_PULSE_MIN_SCALE = 0.5;
const EMPTY_STATE_ICON_SIZE = 64; // px

const COLUMN_HEADER_FONT_SIZE = '0.9375rem'; // 15px

// Shared by the header and every user row so columns line up and each row ends in a divider.
const ROW_SX = {
  display: 'flex',
  gap: 1.5,
  py: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
};

// The filled dot itself breathes (scale + opacity). No expanding ring.
const LIVE_DOT_SX = {
  width: LIVE_DOT_SIZE,
  height: LIVE_DOT_SIZE,
  borderRadius: '50%',
  // Brighter than success.main (#2e7d32) so a live user reads as active, not muted.
  bgcolor: 'success.light',
  flexShrink: 0,
  '@keyframes activeUserDotPulse': {
    '0%, 100%': { transform: `scale(${LIVE_DOT_PULSE_MAX_SCALE})`, opacity: 1 },
    '50%': { transform: `scale(${LIVE_DOT_PULSE_MIN_SCALE})`, opacity: 0.55 },
  },
  animation: `activeUserDotPulse ${LIVE_DOT_PULSE_DURATION_MS}ms ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

const COLUMN_HEADER_SX = {
  fontSize: COLUMN_HEADER_FONT_SIZE,
  fontWeight: 500,
  color: DASHBOARD_COLORS.header,
};

type ActiveUsersDialogProps = {
  open: boolean;
  onClose: () => void;
  users: LiveActiveUser[];
};

export default function ActiveUsersDialog({ open, onClose, users }: ActiveUsersDialogProps) {
  const { t } = useTranslation();
  // Copy first: Array.sort mutates, and the preview fixture is a shared constant.
  const usersByName = [...users].sort(compareLiveActiveUsersByName);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={t(StringKeys.INSIGHTS_ACTIVE_USERS_DIALOG_TITLE)}
      submitButtonText=""
      onSubmit={() => {}}
      footerContent={undefined}
      showFooter={false}
      enableEnterKey={false}
      size="xs"
      sx={FULL_WIDTH_PAPER_SX}
    >
      <Box data-testid="active-users-dialog" sx={{ height: LIST_HEIGHT, overflowY: 'auto' }}>
        {usersByName.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <ErrorIcon sx={{ color: COLORS.TABLE_HEADER_GRAY, fontSize: EMPTY_STATE_ICON_SIZE }} data-testid="active-users-empty-icon" />
            <Typography variant="body1" color="text.disabled" data-testid="active-users-empty">
              {t(StringKeys.INSIGHTS_ACTIVE_USERS_DIALOG_EMPTY)}
            </Typography>
          </Box>
        ) : (
          <Box role="table">
            <Box
              role="row"
              sx={{
                ...ROW_SX,
                position: 'sticky',
                top: 0,
                bgcolor: 'background.paper',
                zIndex: 1,
              }}
            >
              <Typography role="columnheader" variant="body1" sx={{ ...COLUMN_HEADER_SX, flex: 1 }}>
                {t(StringKeys.USER_NAME)}
              </Typography>
              <Typography
                role="columnheader"
                variant="body1"
                sx={{ ...COLUMN_HEADER_SX, width: GROUP_COLUMN_WIDTH, flexShrink: 0 }}
              >
                {t(StringKeys.GROUP)}
              </Typography>
            </Box>
            {usersByName.map((user) => (
              <ActiveUserRow
                key={user.userAuthId}
                user={user}
                unknownName={t(StringKeys.UNKNOWN)}
              />
            ))}
          </Box>
        )}
      </Box>
    </BaseModal>
  );
}

function ActiveUserRow({ user, unknownName }: { user: LiveActiveUser; unknownName: string }) {
  return (
    <Box
      role="row"
      data-testid="active-users-row"
      sx={{ ...ROW_SX, alignItems: 'center' }}
    >
      <Box role="cell" sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
          {user.name ?? unknownName}
        </Typography>
        <Box aria-hidden="true" data-testid="active-users-live-dot" sx={LIVE_DOT_SX} />
      </Box>
      <Typography
        role="cell"
        variant="body2"
        color="text.secondary"
        noWrap
        sx={{ width: GROUP_COLUMN_WIDTH, flexShrink: 0 }}
      >
        {user.groupName}
      </Typography>
    </Box>
  );
}
